import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useRef, useState } from 'react';
import { BILL_ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { useStore } from 'src/stores';

/** The allocation is worked out between calls, so this is how often we ask whether it is done. */
const POLL_INTERVAL_MS = 4000;

/** Roughly three minutes. Past that something is wrong, and asking forever would not fix it. */
const MAX_POLLS = 45;
const EMPTY_STATE = {
  error: null,
  isBusy: false,
  services: []
};
const ALLOCATION_UNREACHABLE = 'The allocation could not be reached. Please try again.';
const ALLOCATION_SLOW = 'The allocation is taking longer than usual. It is still being worked out — close this bill and open it again to ' + 'see where it got to.';

/**
 * Where each service of a bill has got to on its way to being allocated, and the means to move it
 * along. Approving sends the values as they stand on screen; the allocation service creates the bill
 * and then works the table out, so this keeps asking until nothing is left calculating — which is
 * also what approves each table as it becomes ready.
 */
export function useBillAllocations(billId) {
  const store = useStore();
  const _useState = useState(EMPTY_STATE),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    polls = _useState4[0],
    setPolls = _useState4[1];

  // Which bill the answers still coming back belong to, and whether anybody is left to receive
  // them. A request outlives the effect that started it, and counting a poll re-runs that effect,
  // so the effect's own cleanup cannot be what decides whether its answer is still wanted.
  const askedAbout = useRef(billId);
  const isListening = useRef(true);
  useEffect(() => () => {
    isListening.current = false;
  }, []);

  /** True while an answer about this bill is still worth acting on. */
  const isWanted = useCallback(forBillId => isListening.current && askedAbout.current === forBillId, []);
  const apply = useCallback((forBillId, resp) => {
    if (!isWanted(forBillId)) return;

    // A cancelled request answers with nothing, and what is on screen is still true.
    if (!resp) {
      setState(current => _objectSpread(_objectSpread({}, current), {}, {
        isBusy: false
      }));
      return;
    }
    if (!resp.isSuccessful || !resp.response) {
      setState(current => _objectSpread(_objectSpread({}, current), {}, {
        error: ALLOCATION_UNREACHABLE,
        isBusy: false
      }));
      return;
    }
    setState({
      error: null,
      isBusy: false,
      services: resp.response.services || []
    });
  }, [isWanted]);
  const fail = useCallback(forBillId => {
    if (!isWanted(forBillId)) return;
    setState(current => _objectSpread(_objectSpread({}, current), {}, {
      error: ALLOCATION_UNREACHABLE,
      isBusy: false
    }));
  }, [isWanted]);
  useEffect(() => {
    askedAbout.current = billId;
    // Another bill's services are not this one's, however briefly.
    setState(EMPTY_STATE);
    setPolls(0);
    if (!billId) return;
    store.companyBills.getBillAllocationsAsync.run({
      billId
    }).then(resp => apply(billId, resp)).catch(() => fail(billId));
  }, [apply, billId, fail, store]);

  // A table waiting to be approved is still moving: the next call is what approves it.
  const isCalculating = state.services.some(service => [BILL_ALLOCATION_STATE.allocating, BILL_ALLOCATION_STATE.awaitingApproval].includes(service.state));
  const hasGivenUp = polls >= MAX_POLLS;
  useEffect(() => {
    if (!billId || !isCalculating || hasGivenUp) return undefined;
    const timer = setTimeout(() => {
      store.companyBills.allocateBillAsync.run({
        billId
      }).then(resp => apply(billId, resp)).catch(() => fail(billId))
      // Counted once the answer is in, not when the request goes out: this is also what re-arms
      // the loop when a poll comes back saying the same thing as the last one.
      .finally(() => isWanted(billId) && setPolls(count => count + 1));
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [apply, billId, fail, hasGivenUp, isCalculating, isWanted, polls, store]);
  useEffect(() => {
    if (!hasGivenUp || !isCalculating) return;
    setState(current => current.error ? current : _objectSpread(_objectSpread({}, current), {}, {
      error: ALLOCATION_SLOW
    }));
  }, [hasGivenUp, isCalculating]);
  const allocate = useCallback(async services => {
    if (!billId || !(services !== null && services !== void 0 && services.length)) return;
    setPolls(0);
    setState(current => _objectSpread(_objectSpread({}, current), {}, {
      error: null,
      isBusy: true
    }));
    try {
      apply(billId, await store.companyBills.allocateBillAsync.run({
        billId,
        services
      }));
    } catch (_unused) {
      fail(billId);
    }
  }, [apply, billId, fail, store]);
  return {
    allocate,
    error: state.error,
    isBusy: state.isBusy,
    isCalculating: isCalculating && !hasGivenUp,
    services: state.services
  };
}